import z from 'zod'

import { PAYOUT_LABEL } from './payoutTerminology'
import { percentageNumber } from '@components/Form/formValidators'

export const tradingAccountFormSchema = z.object({
  min_trading_days: z
    .number()
    .int('Minimum trading days must be a whole number')
    .min(0, 'Minimum trading days cannot be negative')
    .nullable()
    .optional(),
  max_loss_percentage: percentageNumber,
  profit_target_percentage: percentageNumber,
  max_daily_loss_percentage: percentageNumber,
  max_challenge_duration_days: z
    .number()
    .int('Maximum challenge duration must be a whole number')
    .min(0, 'Maximum challenge duration cannot be negative')
    .nullable()
    .optional(),
  profit_split_percentage: percentageNumber,
  daily_lotsize_limit: z.number().min(0, 'Daily lot size limit cannot be negative').nullable().optional(),
  max_daily_floating_risk: z.number().min(0, 'Maximum daily floating risk cannot be negative').nullable().optional(),
  upgrade_template_id: z
    .number()
    .int('Upgrade template ID must be a whole number')
    .positive('Upgrade template ID must be a positive number')
    .nullable()
    .optional(),
})

export type EditAccountFormValues = z.infer<typeof tradingAccountFormSchema>

const userFormFields = z.object({
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().optional(),
  discount_tier_id: z.number().nullish(),
  paid_customer: z.boolean(),
  affiliate_payouts_on_demand_enabled: z.boolean(),
  competition_manager: z.boolean(),
  can_be_account_manager: z.boolean(),
  account_manager_id: z.string().nullable().optional(),
  referral_code: z.string('Referral code is required').nullable(),
  phone_number_verification_status: z.string('Phone verification status is required'),
  blocked_payout_methods: z.array(z.string()).optional(),
  role: z.string().optional(),
  kycaid_applicant_identifier: z.string().nullable().optional(),
  max_active_accounts: z.number().min(0, 'Maximum active accounts cannot be negative').nullable().optional(),
  max_active_allocation: z.number().min(0, 'Maximum active allocation cannot be negative').nullable().optional(),
  permission_group_ids: z.array(z.string()).optional(),
  // Held as strings so the tri-state select can carry an "inherited" option alongside true/false;
  // mapped back to boolean/null on submit.
  controls: z.record(z.string(), z.string()).optional(),
})

export type UserFormValues = z.infer<typeof userFormFields>

/** A user may sit on no discount tier, on every brand. */
export const userFormSchema = userFormFields

/** `email` is hidden without the `users.credentials` permission, so only validate its format when it's shown. */
export const getUserFormSchema = (isEmailEditable: boolean) =>
  isEmailEditable
    ? userFormFields.extend({ email: z.string().trim().pipe(z.email('Enter a valid email address')) })
    : userFormSchema

export const payoutFormSchema = z.object({
  amount: z.number().positive(`${PAYOUT_LABEL} amount must be positive`).optional(),
})

export type PayoutFormValues = z.infer<typeof payoutFormSchema>
